/**
 * Prompt Edit Page
 *
 * Edit page for LLM prompts with Monaco Editor, language tabs, and model configuration
 *
 * Acceptance Criteria: AC 4
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import toast from 'react-hot-toast';
import {
  usePrompt,
  useUpdatePrompt,
  type PromptTemplate,
  type ModelConfig,
} from '../services/promptsApi';
import { useInitialData } from 'contexts/InitialDataContext';

export function PromptEditView() {
  const { promptKey } = useParams<{ promptKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = '/prompts' + (location.state?.searchParams || '');

  const { state: initialDataState } = useInitialData();
  const { data: prompt, isLoading, error } = usePrompt(promptKey);
  const updatePromptMutation = useUpdatePrompt();

  // Get available languages from backend
  const languages = initialDataState.data?.languages || [];

  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [modelConfig, setModelConfig] = useState<ModelConfig>({ model: 'gpt-4' });
  const [modelConfigJson, setModelConfigJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [provider, setProvider] = useState<string>('openai');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAddLanguageDropdown, setShowAddLanguageDropdown] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);

  // Initialize form data when prompt loads
  useEffect(() => {
    if (prompt) {
      setTemplates(prompt.promptTemplates);
      setModelConfig(prompt.modelConfig);
      setModelConfigJson(JSON.stringify(prompt.modelConfig, null, 2));
      setDescription(prompt.description);
      setProvider(prompt.provider);
    }
  }, [prompt]);

  // Track unsaved changes
  useEffect(() => {
    if (!prompt) return;

    const hasChanges =
      JSON.stringify(templates) !== JSON.stringify(prompt.promptTemplates) ||
      JSON.stringify(modelConfig) !== JSON.stringify(prompt.modelConfig) ||
      description !== prompt.description ||
      provider !== prompt.provider;

    setHasUnsavedChanges(hasChanges);
  }, [templates, modelConfig, description, provider, prompt]);

  const handleTemplateChange = (languageCode: string, newTemplate: string) => {
    setTemplates((prev) =>
      prev.map((t) => (t.languageCode === languageCode ? { ...t, template: newTemplate } : t))
    );
  };

  const handleAddLanguage = (languageCode: string) => {
    // Check if language already exists
    if (templates.some((t) => t.languageCode === languageCode)) {
      toast.error('Language already added');
      return;
    }

    setTemplates((prev) => [...prev, { languageCode, template: '' }]);
    setActiveTab(templates.length); // Switch to new tab
    setShowAddLanguageDropdown(false);
  };

  const handleRemoveLanguage = (languageCode: string) => {
    if (templates.length <= 1) {
      toast.error('At least one language is required');
      return;
    }

    setTemplates((prev) => prev.filter((t) => t.languageCode !== languageCode));

    // Adjust active tab if needed
    if (activeTab >= templates.length - 1) {
      setActiveTab(Math.max(0, templates.length - 2));
    }
  };

  const validateModelConfigJson = (jsonString: string): string | null => {
    try {
      const parsed = JSON.parse(jsonString) as Record<string, unknown>;
      if (!parsed.model || typeof parsed.model !== 'string' || !parsed.model.trim()) {
        return "The 'model' key is required and must be a non-empty string";
      }
      return null; // Valid
    } catch {
      return 'Invalid JSON syntax';
    }
  };

  const handleJsonChange = (value: string | undefined) => {
    const newValue = value || '';
    setModelConfigJson(newValue);

    // Validate JSON
    const error = validateModelConfigJson(newValue);
    setJsonError(error);

    // Update modelConfig if valid
    if (!error) {
      try {
        const parsed = JSON.parse(newValue) as ModelConfig;
        setModelConfig(parsed);
      } catch {
        // Should not happen since we validated above
      }
    }
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(modelConfigJson);
      const formatted = JSON.stringify(parsed, null, 2);
      setModelConfigJson(formatted);
      setJsonError(null);
    } catch {
      toast.error('Cannot format invalid JSON');
    }
  };

  const handleSave = async () => {
    if (!promptKey) return;

    // Validation
    if (templates.length === 0) {
      toast.error('At least one language template is required');
      return;
    }

    if (templates.some((t) => !t.template.trim())) {
      toast.error('All language templates must have content');
      return;
    }

    // Validate JSON
    if (jsonError) {
      toast.error(jsonError);
      return;
    }

    try {
      await updatePromptMutation.mutateAsync({
        promptKey,
        update: {
          promptTemplates: templates,
          modelConfig,
          description,
          provider,
        },
      });

      toast.success('Prompt updated successfully!');
      setHasUnsavedChanges(false);
    } catch {
      toast.error('Failed to update prompt');
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) return;
    }

    navigate(backTo);
  };

  const availableLanguages = languages.filter(
    (lang) => !templates.some((t) => t.languageCode === lang.code)
  );

  if (isLoading || initialDataState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !prompt) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading prompt</p>
          <p className="text-gray-600">{error?.message || 'Prompt not found'}</p>
          <button
            onClick={() => navigate(backTo)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Prompts
          </button>
        </div>
      </div>
    );
  }

  const currentTemplate = templates[activeTab];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-4">
            <span className="px-4 py-2 bg-blue-100 text-blue-800 text-lg font-semibold rounded">
              {prompt.promptKey}
            </span>
            <span className="text-sm text-gray-500">
              Last updated: {new Date(prompt.updatedAt).toLocaleString()}
            </span>
          </div>
          <button
            onClick={handleCancel}
            className="text-blue-600 hover:text-blue-800 font-medium"
            data-testid="back-button"
          >
            ← Back to Prompts
          </button>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column: Prompt Editor (70%) */}
        <div className="col-span-12 lg:col-span-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Prompt Templates</h2>

            {/* Language Tabs */}
            <div className="flex items-center gap-2 mb-4 border-b border-gray-200">
              {templates.map((template, index) => (
                <div key={template.languageCode} className="flex items-center">
                  <button
                    onClick={() => setActiveTab(index)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === index
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`language-tab-${template.languageCode}`}
                  >
                    {template.languageCode.toUpperCase()}
                  </button>
                  {templates.length > 1 && (
                    <button
                      onClick={() => handleRemoveLanguage(template.languageCode)}
                      className="ml-1 text-red-600 hover:text-red-800 text-xs"
                      title="Remove language"
                      data-testid={`remove-language-${template.languageCode}`}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}

              {/* Add Language Button */}
              <div className="relative ml-auto">
                <button
                  onClick={() => setShowAddLanguageDropdown(!showAddLanguageDropdown)}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  data-testid="add-language-button"
                >
                  + Add Language
                </button>

                {showAddLanguageDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 max-h-60 overflow-y-auto">
                    {availableLanguages.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500">All languages added</div>
                    ) : (
                      availableLanguages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleAddLanguage(lang.code)}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          data-testid={`add-language-option-${lang.code}`}
                        >
                          {lang.name} ({lang.code})
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Monaco Editor */}
            {currentTemplate && (
              <div className="mb-4">
                <div className="mb-2 text-sm text-gray-600">
                  Characters: {currentTemplate.template.length}
                </div>
                <div className="border border-gray-300 rounded-md overflow-hidden">
                  <Editor
                    height="400px"
                    language="plaintext"
                    theme="vs-light"
                    value={currentTemplate.template}
                    onChange={(value) =>
                      handleTemplateChange(currentTemplate.languageCode, value || '')
                    }
                    options={{
                      minimap: { enabled: false },
                      lineNumbers: 'on',
                      wordWrap: 'on',
                      fontSize: 14,
                      scrollBeyondLastLine: false,
                    }}
                  />
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Use placeholders like {'{'}episode_idea{'}'}, {'{'}target_complexity{'}'} in your
                  template
                </div>
              </div>
            )}

            {/* Description */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={description || ''}
                onChange={(e) => setDescription(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Brief description of this prompt's purpose..."
                data-testid="description-input"
              />
            </div>
          </div>
        </div>

        {/* Right Column: Model Configuration (30%) */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            {/* Provider */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">LLM Provider</label>
              <input
                type="text"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="openai"
                data-testid="provider-input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Provider name (e.g., openai, deepseek). Requires matching {'{PROVIDER}_API_KEY'} env
                var.
              </p>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Model Configuration</h2>
              <button
                onClick={handleFormatJson}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                data-testid="format-json-button"
              >
                Format JSON
              </button>
            </div>

            {/* Info Box */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <p className="text-blue-800 mb-1">
                <strong>Note:</strong> The <code className="bg-blue-100 px-1 rounded">model</code>{' '}
                key is required. All other keys are optional.
              </p>
              <button
                onClick={() => setShowHelpText(!showHelpText)}
                className="text-blue-600 hover:text-blue-800 text-xs underline"
                data-testid="toggle-help-button"
              >
                {showHelpText ? 'Hide' : 'Show'} Examples
              </button>
            </div>

            {/* Help Text - Example Configurations */}
            {showHelpText && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded text-xs">
                <h4 className="font-semibold mb-2">Example Configurations:</h4>
                <div className="space-y-2">
                  <div>
                    <strong>Minimal:</strong>
                    <pre className="bg-white p-2 rounded mt-1 overflow-x-auto">
                      {`{"model": "gpt-4", "max_completion_tokens": 2000}`}
                    </pre>
                  </div>
                  <div>
                    <strong>OpenAI GPT-4 Basic:</strong>
                    <pre className="bg-white p-2 rounded mt-1 overflow-x-auto">
                      {`{"model": "gpt-4", "temperature": 0.7, "max_completion_tokens": 4000}`}
                    </pre>
                  </div>
                  <div>
                    <strong>Claude Sonnet:</strong>
                    <pre className="bg-white p-2 rounded mt-1 overflow-x-auto">
                      {`{"model": "claude-sonnet-4", "temperature": 0.7, "max_tokens": 4000}`}
                    </pre>
                  </div>
                </div>
              </div>
            )}

            {/* JSON Editor */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Configuration JSON
              </label>
              <div className="border border-gray-300 rounded-md overflow-hidden">
                <Editor
                  height="300px"
                  language="json"
                  theme="vs-light"
                  value={modelConfigJson}
                  onChange={handleJsonChange}
                  options={{
                    minimap: { enabled: false },
                    lineNumbers: 'on',
                    wordWrap: 'on',
                    fontSize: 14,
                    scrollBeyondLastLine: false,
                    formatOnPaste: true,
                    tabSize: 2,
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Characters: {modelConfigJson.length}</span>
                <span>Lines: {modelConfigJson.split('\n').length}</span>
              </div>
              {jsonError && (
                <div className="mt-2 text-sm text-red-600" data-testid="json-error">
                  ⚠️ {jsonError}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={handleSave}
                disabled={!hasUnsavedChanges || updatePromptMutation.isPending || !!jsonError}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                data-testid="save-button"
              >
                {updatePromptMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="cancel-button"
              >
                Cancel
              </button>
            </div>

            {hasUnsavedChanges && (
              <div className="mt-4 text-sm text-orange-600">⚠️ You have unsaved changes</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
